import React, { useState, useEffect, useContext, useRef } from "react";
import { AuthContext } from "../../../context/authcontext";
import { API_BASE_URL } from "../../../environment";
import { getAuthHeaders } from "../../../utils/authUtils";
import { cachedRequest } from "../../../utils/requestCache";
import { useFilterOptions } from "../../../hooks/useFilterOptions";
import { languageOptions } from "../../../components/language";
import { currencyOptions } from "../../../components/currency";
import { timezoneOptions } from "../../../components/timezone";

interface CompanyContactFilterDropdownProps {
  onFilterChange: (filters: Record<string, any>) => void;
  currentFilters: Record<string, any>;
  loading?: boolean;
  entityType: "company" | "contact";
}

interface Team {
  team_id: number;
  name: string;
  manager_id: number;
  created_by: {
    user_id: number;
    full_name: string;
    avatar_url: string;
  };
  tenant_id: number;
  total_team_members: number;
  manager: {
    user_id: number;
    full_name: string;
    avatar_url: string;
  };
  tenant: {
    tenant_id: number;
    tenant_name: string;
    domain_name: string;
  };
}

interface User {
  tenant_user_id: number;
  user_id: number;
  user_first_name: string;
  user_last_name: string;
  role_display_name: string;
}

const SOURCE_OPTIONS = [
  "Email",
  "Call",
  "LINE",
  "Facebook",
  "Website",
  "Partner",
  "Reference",
];

const CompanyContactFilterDropdown: React.FC<
  CompanyContactFilterDropdownProps
> = ({ onFilterChange, currentFilters, loading = false, entityType }) => {
  const { selectedTenantId } = useContext(AuthContext);
  const [isOpen, setIsOpen] = useState(false);
  const { companies, industryTypes, businessTypes } = useFilterOptions(selectedTenantId, {
    companies: true,
    industryTypes: true,
    businessTypes: true,
  }, isOpen);
  const [teams, setTeams] = useState<Team[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [isLoadingTeams, setIsLoadingTeams] = useState(false);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [expandedFilters, setExpandedFilters] = useState<Set<string>>(
    new Set()
  );
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [teamFilter, setTeamFilter] = useState(currentFilters.teamFilter);
  const [userFilter, setUserFilter] = useState(currentFilters.userFilter);
  const [sourceFilter, setSourceFilter] = useState(currentFilters.sourceFilter);
  const [dynamicFilters, setDynamicFilters] = useState<Record<string, any>>(currentFilters);

  useEffect(() => {
    setTeamFilter(currentFilters.teamFilter);
  }, [currentFilters.teamFilter]);

  useEffect(() => {
    setUserFilter(currentFilters.userFilter);
  }, [currentFilters.userFilter]);

  useEffect(() => {
    setSourceFilter(currentFilters.sourceFilter);
  }, [currentFilters.sourceFilter]);

  const fetchTeams = async () => {
    if (!selectedTenantId) return;

    setIsLoadingTeams(true);
    try {
      const teams = await cachedRequest(`filter-teams:${selectedTenantId}`, async () => {
        const response = await fetch(
          `${API_BASE_URL}/team/?tenant_id=${selectedTenantId}&response_mode=light`,
          {
            headers: getAuthHeaders(),
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch teams");
        }

        const data = await response.json();
        return data.results || [];
      });
      setTeams(teams);
    } catch (error) {
      console.error("Error fetching teams:", error);
    } finally {
      setIsLoadingTeams(false);
    }
  };

  const fetchUsers = async () => {
    if (!selectedTenantId) return;

    setIsLoadingUsers(true);
    try {
      const results = await cachedRequest(`filter-tenant-users:${selectedTenantId}`, async () => {
        const response = await fetch(
          `${API_BASE_URL}/tenant-user/?tenant_id=${selectedTenantId}&page=1&page_size=200&response_mode=light`,
          {
            headers: getAuthHeaders(),
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch users");
        }

        const data = await response.json();
        return data.results || [];
      });
      setUsers(results);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setIsLoadingUsers(false);
    }
  };

  useEffect(() => {
    if (selectedTenantId && isOpen) {
      fetchTeams();
      fetchUsers();
    }
  }, [selectedTenantId, isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleDropdown = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (isOpen) e.currentTarget.blur();
    setIsOpen(!isOpen);
  };

  const toggleFilterExpansion = (filterKey: string) => {
    const newExpandedFilters = new Set(expandedFilters);
    if (newExpandedFilters.has(filterKey)) {
      newExpandedFilters.delete(filterKey);
    } else {
      newExpandedFilters.add(filterKey);
    }
    setExpandedFilters(newExpandedFilters);
  };

  const getSalesUserId = () => {
    try {
      const authRaw = localStorage.getItem("authResponse");
      const tenantId = localStorage.getItem("selectedTenantId");
      if (authRaw && tenantId) {
        const auth = JSON.parse(authRaw);
        const tenantObj = Array.isArray(auth.tenants)
          ? auth.tenants.find((t: any) => String(t.tenant_id) === tenantId)
          : null;
        if (tenantObj?.role_key === "SALESUSER" && auth.user_id) {
          return auth.user_id;
        }
      }
    } catch (e) {
      console.error(e);
    }
    return null;
  };

  const handleTeamChange = (teamId: number | null) => {
    setTeamFilter(teamId);
    const payload: Record<string, any> = {
      ...dynamicFilters,
      teamFilter: teamId,
      userFilter,
      sourceFilter,
    };
    const salesUserId = getSalesUserId();
    if (salesUserId) payload.sales_user_id = salesUserId;
    onFilterChange(payload);
  };

  const handleUserChange = (userId: number | null) => {
    setUserFilter(userId);
    const payload: Record<string, any> = {
      ...dynamicFilters,
      teamFilter,
      userFilter: userId,
      sourceFilter,
    };
    const salesUserId = getSalesUserId();
    if (salesUserId) payload.sales_user_id = salesUserId;
    onFilterChange(payload);
  };

  const handleSourceChange = (source: string | null) => {
    setSourceFilter(source);
    const payload: Record<string, any> = {
      ...dynamicFilters,
      teamFilter,
      userFilter,
      sourceFilter: source,
    };
    const salesUserId = getSalesUserId();
    if (salesUserId) payload.sales_user_id = salesUserId;
    onFilterChange(payload);
  };

  const handleDynamicFilterChange = (key: string, value: any) => {
    const updatedFilters = { ...dynamicFilters, [key]: value };
    setDynamicFilters(updatedFilters);
    const payload: Record<string, any> = {
      ...updatedFilters,
      teamFilter,
      userFilter,
      sourceFilter,
    };
    const salesUserId = getSalesUserId();
    if (salesUserId) payload.sales_user_id = salesUserId;
    onFilterChange(payload);
  };

  const applyFilters = () => {
    setIsOpen(false);
  };

  const resetFilters = () => {
    setTeamFilter(null);
    setUserFilter(null);
    setSourceFilter(null);
    setDynamicFilters({});
    const payload: Record<string, any> = {
      teamFilter: null,
      userFilter: null,
      sourceFilter: null,
    };
    const salesUserId = getSalesUserId();
    if (salesUserId) payload.sales_user_id = salesUserId;
    onFilterChange(payload);
    setIsOpen(false);
  };

  const hasFormChanges =
    teamFilter !== currentFilters.teamFilter ||
    userFilter !== currentFilters.userFilter ||
    sourceFilter !== currentFilters.sourceFilter;

  const getActiveFilterCount = () => {
    let count = 0;
    if (currentFilters.teamFilter !== null && currentFilters.teamFilter !== undefined) count++;
    if (currentFilters.userFilter !== null && currentFilters.userFilter !== undefined) count++;
    if (currentFilters.sourceFilter !== null && currentFilters.sourceFilter !== undefined && currentFilters.sourceFilter !== "") count++;

    Object.keys(currentFilters).forEach((key) => {
      if (!["searchTerm", "dateRange", "statusFilter", "archivedFilter", "is_active", "sortBy", "sortOrder", "teamFilter", "userFilter", "sourceFilter", "sales_user_id"].includes(key)) {
        const val = currentFilters[key];
        if (Array.isArray(val) && val.length === 0) return;
        if (val !== null && val !== undefined && val !== "") count++;
      }
    });

    return count;
  };

  const [textFilters, setTextFilters] = useState<Record<string, string>>({});

  useEffect(() => {
    setDynamicFilters(currentFilters);
    setTextFilters((prev) => {
      const next = Object.fromEntries(
        Object.entries(prev).filter(
          ([key]) => currentFilters[key] !== undefined && currentFilters[key] !== null && currentFilters[key] !== ""
        )
      );
      return Object.keys(next).length === Object.keys(prev).length ? prev : next;
    });
  }, [currentFilters]);
  const debounceTimers = useRef<Record<string, NodeJS.Timeout>>({});

  const handleTextChange = (key: string, value: string) => {
    setTextFilters((prev) => ({ ...prev, [key]: value }));

    if (debounceTimers.current[key]) {
      clearTimeout(debounceTimers.current[key]);
    }

    debounceTimers.current[key] = setTimeout(() => {
      handleDynamicFilterChange(key, value);
    }, 2000);
  };

  const activeFilterCount = getActiveFilterCount();

  const truncateText = (text: string, maxLength: number = 15) => {
    if (!text) return "";
    return text.length > maxLength ? text.substring(0, maxLength) + "..." : text;
  };

  return (
    <div className="position-relative" ref={dropdownRef}>
      <button
        type="button"
        className={`btn ${isOpen ? "btn-primary" : "btn-outline-light"} shadow dropdown-toggle mx-2 position-relative`}
        onClick={toggleDropdown}
      >
        <img
          src="/assets/img/icons/filter.svg"
          alt="Filter"
          className="me-1"
          style={{
            width: "16px",
            height: "16px",
            filter: isOpen ? "brightness(0) invert(1)" : undefined,
          }}
        />
        <span className="me-2">Filter</span>
        {activeFilterCount > 0 && (
          <span className="position-absolute top-0 start-100 translate-middle p-1 bg-danger border border-light rounded-circle">
            <span className="visually-hidden">New alerts</span>
          </span>
        )}
      </button>

      {isOpen && (
        <div
          className="dropdown-menu show"
          style={{
            width: "280px",
            maxHeight: "500px",
            zIndex: 9999,
            position: "absolute",
            top: "100%",
            right: "0",
            left: "auto",
            marginTop: "0.125rem",
            backgroundColor: "white",
            border: "1px solid rgba(0,0,0,.15)",
            borderRadius: "0.375rem",
            boxShadow: "0 0.5rem 1rem rgba(0, 0, 0, 0.15)",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Header */}
          <div className="p-3 pb-0">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="mb-0">
                <img
                  src="/assets/img/icons/filter.svg"
                  alt="Filter"
                  className="me-1"
                  style={{ width: "16px", height: "16px" }}
                />
                {entityType === "company" ? "Company" : "Contact"} Filters
              </h6>
            </div>
          </div>

          {/* Scrollable Filter Content */}
          <div
            className="px-3"
            style={{ maxHeight: "380px", overflowY: "auto", flex: 1 }}
          >
            <div className="space-y-2">
              {/* Assigned Team Filter */}
              <div className="border rounded">
                <button
                  type="button"
                  className="w-100 btn btn-link text-start text-decoration-none p-2 d-flex justify-content-between align-items-center"
                  onClick={() => toggleFilterExpansion("team")}
                  disabled={loading || isLoadingTeams}
                >
                  <span className="small text-dark">
                    <strong>Assigned Team</strong>
                  </span>
                  <div className="d-flex align-items-center">
                    {teamFilter !== null && teams.find((t) => t.team_id === teamFilter)?.name && (
                      <span className="small text-muted me-2" title={teams.find((t) => t.team_id === teamFilter)?.name}>
                        {truncateText(teams.find((t) => t.team_id === teamFilter)?.name || "")}
                      </span>
                    )}
                    <i
                      className={`ti ti-chevron-${expandedFilters.has("team") ? "up" : "down"
                        } small text-dark`}
                    ></i>
                  </div>
                </button>
                {expandedFilters.has("team") && (
                  <div className="p-2 border-top">
                    <select
                      className="form-select form-select-sm"
                      value={teamFilter || ""}
                      onChange={(e) =>
                        handleTeamChange(
                          e.target.value ? Number(e.target.value) : null
                        )
                      }
                      disabled={loading || isLoadingTeams}
                    >
                      <option value="">All Teams</option>
                      {teams.map((team) => (
                        <option key={team.team_id} value={team.team_id}>
                          {team.name}
                        </option>
                      ))}
                    </select>
                    {isLoadingTeams && (
                      <div className="text-center mt-2">
                        <div
                          className="spinner-border spinner-border-sm text-primary"
                          role="status"
                        >
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Assigned User Filter */}
              <div className="border rounded mt-2">
                <button
                  type="button"
                  className="w-100 btn btn-link text-start text-decoration-none p-2 d-flex justify-content-between align-items-center"
                  onClick={() => toggleFilterExpansion("user")}
                  disabled={loading || isLoadingUsers}
                >
                  <span className="small text-dark">
                    <strong>Assigned User</strong>
                  </span>
                  <div className="d-flex align-items-center">
                    {userFilter !== null && users.find((u) => u.user_id === userFilter) && (
                      <span className="small text-muted me-2" title={`${users.find((u) => u.user_id === userFilter)?.user_first_name} ${users.find((u) => u.user_id === userFilter)?.user_last_name}`}>
                        {truncateText(`${users.find((u) => u.user_id === userFilter)?.user_first_name} ${users.find((u) => u.user_id === userFilter)?.user_last_name}`)}
                      </span>
                    )}
                    <i
                      className={`ti ti-chevron-${expandedFilters.has("user") ? "up" : "down"
                        } small text-dark`}
                    ></i>
                  </div>
                </button>
                {expandedFilters.has("user") && (
                  <div className="p-2 border-top">
                    <select
                      className="form-select form-select-sm"
                      value={userFilter || ""}
                      onChange={(e) =>
                        handleUserChange(
                          e.target.value ? Number(e.target.value) : null
                        )
                      }
                      disabled={loading || isLoadingUsers}
                    >
                      <option value="">All Users</option>
                      {users.map((user) => (
                        <option key={user.tenant_user_id} value={user.user_id}>
                          {user.user_first_name} {user.user_last_name}
                        </option>
                      ))}
                    </select>
                    {isLoadingUsers && (
                      <div className="text-center mt-2">
                        <div
                          className="spinner-border spinner-border-sm text-primary"
                          role="status"
                        >
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Source Filter */}
              {entityType === "contact" && (
                <div className="border rounded mt-2">
                  <button
                    type="button"
                    className="w-100 btn btn-link text-start text-decoration-none p-2 d-flex justify-content-between align-items-center"
                    onClick={() => toggleFilterExpansion("source")}
                    disabled={loading}
                  >
                    <span className="small text-dark">
                      <strong>Source</strong>
                    </span>
                    <div className="d-flex align-items-center">
                      {sourceFilter && (
                        <span className="small text-muted me-2" title={sourceFilter}>
                          {truncateText(sourceFilter)}
                        </span>
                      )}
                      <i
                        className={`ti ti-chevron-${expandedFilters.has("source") ? "up" : "down"
                          } small text-dark`}
                      ></i>
                    </div>
                  </button>
                  {expandedFilters.has("source") && (
                    <div className="p-2 border-top">
                      <select
                        className="form-select form-select-sm"
                        value={sourceFilter || ""}
                        onChange={(e) =>
                          handleSourceChange(e.target.value || null)
                        }
                        disabled={loading}
                      >
                        <option value="">All Sources</option>
                        {SOURCE_OPTIONS.map((source) => (
                          <option key={source} value={source}>
                            {source}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              )}

              {/* Dynamic UI Filters for Company */}
              {entityType === "company" && (
                <>
                  {[
                    "Phone",
                    "Country",
                    "Website",
                    "Address",
                    "City",
                    "State",
                    "Annual Revenue",
                    "Business Type",
                    "Industry Type",
                    "Tags",
                    // "Custom Fields", // Commented out as requested
                  ].map((field) => {
                    const fieldKey = field === "Industry Type" ? "industry_type_id"
                      : field === "Business Type" ? "business_type_id"
                        : field.toLowerCase().replace(/ /g, "_");
                    const filterValue = dynamicFilters[fieldKey];
                    let displayValue = "";
                    if (filterValue !== undefined && filterValue !== null && filterValue !== "") {
                      if (field === "Industry Type") {
                        displayValue = industryTypes.find(opt => String(opt.value) === String(filterValue))?.label || "";
                      } else if (field === "Business Type") {
                        displayValue = businessTypes.find(opt => String(opt.value) === String(filterValue))?.label || "";
                      } else {
                        displayValue = String(filterValue);
                      }
                    }

                    return (
                      <div className="border rounded mt-2" key={field}>
                        <button
                          type="button"
                          className="w-100 btn btn-link text-start text-decoration-none p-2 d-flex justify-content-between align-items-center"
                          onClick={() => toggleFilterExpansion(field)}
                        >
                          <span className="small text-dark">
                            <strong>{field}</strong>
                          </span>
                          <div className="d-flex align-items-center">
                            {displayValue && (
                              <span className="small text-muted me-2" title={displayValue}>
                                {truncateText(displayValue)}
                              </span>
                            )}
                            <i
                              className={`ti ti-chevron-${expandedFilters.has(field) ? "up" : "down"
                                } small text-dark`}
                            ></i>
                          </div>
                        </button>
                        {expandedFilters.has(field) && (
                          <div className="p-2 border-top">
                            {field === "Industry Type" ? (
                              <select
                                className="form-select form-select-sm"
                                value={dynamicFilters.industry_type_id || ""}
                                onChange={(e) => handleDynamicFilterChange("industry_type_id", e.target.value)}
                              >
                                <option value="">Select Industry Type</option>
                                {industryTypes.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                              </select>
                            ) : field === "Business Type" ? (
                              <select
                                className="form-select form-select-sm"
                                value={dynamicFilters.business_type_id || ""}
                                onChange={(e) => handleDynamicFilterChange("business_type_id", e.target.value)}
                              >
                                <option value="">Select Business Type</option>
                                {businessTypes.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                              </select>
                            ) : (
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                placeholder={`Enter ${field}`}
                                value={textFilters[field.toLowerCase().replace(/ /g, "_")] !== undefined ? textFilters[field.toLowerCase().replace(/ /g, "_")] : (dynamicFilters[field.toLowerCase().replace(/ /g, "_")] || "")}
                                onChange={(e) => handleTextChange(field.toLowerCase().replace(/ /g, "_"), e.target.value)}
                              />
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </>
              )}

              {/* Dynamic UI Filters for Contact */}
              {entityType === "contact" && (
                <>
                  {[
                    "Phone",
                    "Email",
                    "Tags",
                    "Preferred Language",
                    "Company",
                    "Engagement Source",
                    "Active / In Active",
                    // "Custom Fields", // Commented out as requested
                  ].map((field) => {
                    const fieldKey = field === "Company" ? "company_id"
                      : field === "Preferred Language" ? "preferred_language"
                        : field === "Active / In Active" ? "is_active"
                          : field === "Engagement Source" ? "engagement_source"
                            : field.toLowerCase().replace(/ /g, "_");
                    const filterValue = dynamicFilters[fieldKey];
                    let displayValue = "";
                    if (filterValue !== undefined && filterValue !== null && filterValue !== "") {
                      if (field === "Company") {
                        displayValue = companies.find(opt => String(opt.value) === String(filterValue))?.label || "";
                      } else if (field === "Preferred Language") {
                        displayValue = languageOptions.find(opt => String(opt.value) === String(filterValue))?.label || "";
                      } else if (field === "Active / In Active") {
                        displayValue = String(filterValue) === "true" ? "Active" : "In Active";
                      } else {
                        displayValue = String(filterValue);
                      }
                    }

                    return (
                      <div className="border rounded mt-2" key={field}>
                        <button
                          type="button"
                          className="w-100 btn btn-link text-start text-decoration-none p-2 d-flex justify-content-between align-items-center"
                          onClick={() => toggleFilterExpansion(field)}
                        >
                          <span className="small text-dark">
                            <strong>{field}</strong>
                          </span>
                          <div className="d-flex align-items-center">
                            {displayValue && (
                              <span className="small text-muted me-2" title={displayValue}>
                                {truncateText(displayValue)}
                              </span>
                            )}
                            <i
                              className={`ti ti-chevron-${expandedFilters.has(field) ? "up" : "down"
                                } small text-dark`}
                            ></i>
                          </div>
                        </button>
                        {expandedFilters.has(field) && (
                          <div className="p-2 border-top">
                            {field === "Preferred Language" ? (
                              <select
                                className="form-select form-select-sm"
                                value={dynamicFilters.preferred_language || ""}
                                onChange={(e) => handleDynamicFilterChange("preferred_language", e.target.value)}
                              >
                                <option value="">Select Language</option>
                                {languageOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                              </select>
                            ) : field === "Active / In Active" ? (
                              <select
                                className="form-select form-select-sm"
                                value={dynamicFilters.is_active !== undefined ? String(dynamicFilters.is_active) : ""}
                                onChange={(e) => handleDynamicFilterChange("is_active", e.target.value)}
                              >
                                <option value="">Select Status</option>
                                <option value="true">Active</option>
                                <option value="false">In Active</option>
                              </select>
                            ) : field === "Company" ? (
                              <select
                                className="form-select form-select-sm"
                                value={dynamicFilters.company_id || ""}
                                onChange={(e) => handleDynamicFilterChange("company_id", e.target.value)}
                              >
                                <option value="">Select Company</option>
                                {companies.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                              </select>
                            ) : field === "Engagement Source" ? (
                              <select
                                className="form-select form-select-sm"
                                value={dynamicFilters.engagement_source || ""}
                                onChange={(e) => handleDynamicFilterChange("engagement_source", e.target.value)}
                              >
                                <option value="">Select Source</option>
                                {SOURCE_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                              </select>
                            ) : (
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                placeholder={`Enter ${field}`}
                                value={textFilters[fieldKey] !== undefined ? textFilters[fieldKey] : (dynamicFilters[fieldKey] || "")}
                                onChange={(e) => handleTextChange(fieldKey, e.target.value)}
                              />
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          </div>
          <br></br>

          {/* Action Buttons */}
          {activeFilterCount > 0 && (
            <div
              className="p-3 pt-2 border-top"
              style={{ backgroundColor: "white" }}
            >
              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm flex-fill"
                  onClick={resetFilters}
                  disabled={loading}
                >
                  <i className="ti ti-refresh me-1"></i>
                  Reset
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CompanyContactFilterDropdown;

